import { Component, OnInit, Input, OnDestroy, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { EMPTY, Subject, map, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { CliListEntry } from 'src/app/shared/cli-list-entry';
import { ClientApiService } from 'src/app/services/client-api.service';
import { AgentPasteToCreateComponent } from '../agent-paste-to-create/agent-paste-to-create.component';
import { AgentQuickCreateResult } from '../agent-customer-quick-create/agent-customer-quick-create.component';
import { AgentCustomerTier } from 'src/app/shared/agent-admin';
import { environment } from '../../../environments/environment';

/**
 * Reusable company-picker dropdown for use in document editing panels.
 *
 * Key collaborator: {@link ClientApiService} — loads the company list via
 * `getCliList()` / `getCliChildList(parentCompany)` and resolves the main company ID
 * via `getMainIdByClientId(id)`.
 *
 * **Data-flow:** `parentCompany` change → `_reloadComponentData()` → list loaded →
 * `_clientMC` resolved by `selectedID` → `_selectCompany()` optionally triggers
 * `clientId$` → `getMainIdByClientId` → `selectedMainCompanyID` emitted.
 *
 * **Inputs summary:**
 * | Field              | Purpose                                                              |
 * |--------------------|----------------------------------------------------------------------|
 * | `parentCompany`    | When set, list is filtered to children of this company               |
 * | `selectedID`       | Pre-selects the matching entry after the list loads                  |
 * | `disabled`         | Passes through to the PrimeNG dropdown and action buttons            |
 * | `readonly`         | Prevents selection change without hiding the dropdown                |
 * | `labelKey`         | Display label rendered above the dropdown                            |
 * | `placeholder`      | Dropdown placeholder text                                            |
 * | `showLabel`        | Toggles label visibility                                             |
 * | `showEditButton`   | Toggles the pencil/edit action button                                |
 * | `showAddButton`    | Toggles the plus/add action button                                   |
 * | `showSearchButton` | Toggles the search action button                                     |
 *
 * **Outputs:**
 * - `selectedCompanyID` — emits the selected company's `CLIID` on change or clear.
 * - `selectedMainCompanyID` — emits the main-company `CLIID` when the selected company
 *   has `CASTCATID > 0`.
 */
@Component({
  selector: 'app-companyselector',
  templateUrl: './companyselector.component.html',
  styleUrls: ['./companyselector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class CompanyselectorComponent implements OnInit, OnChanges, OnDestroy {

  _clientslistMC: CliListEntry[];
  _clientMC: CliListEntry;
  _loading: boolean;

  /** Guards against a double initial load. `ngOnChanges` runs before
   *  `ngOnInit` and already triggers `_reloadComponentData()` for every
   *  caller that binds `[parentCompany]` (every existing one does, even
   *  as `[parentCompany]="0"` for an unscoped list). A caller that omits
   *  the binding entirely -- the deliberate "no [parentCompany]" case --
   *  never produces a `parentCompany` entry in `ngOnChanges`'s
   *  `SimpleChanges`, so without this flag the list would never load at
   *  all. `ngOnInit` uses it to run the load exactly once either way. */
  private _dataLoaded: boolean = false;

  @ViewChild('pasteDialog') pasteToCreateDialog: AgentPasteToCreateComponent;

  private readonly clientId$ = new Subject<number>();
  private readonly destroy$ = new Subject<void>();
  private readonly _reloadCancel$ = new Subject<void>();

  totalRecordsMC: number;
  searchDisplay: boolean = false;

  /** Menu items for the "Add" button split-button — two choices. */
  addMenuItems: MenuItem[] = [
    { label: 'Розпізнати з тексту', icon: 'pi pi-search', command: () => this.onAddPasteToCreate() },
  ];

  /** Display label rendered above the dropdown. */
  @Input() labelKey = 'Продавец';

  /** Dropdown placeholder text. */
  @Input() placeholder = 'Выбрать компанию...';

  /** When set, the list is filtered to children of this company; reloads on every change. */
  @Input() parentCompany: number = null;

  /** Pre-selects the matching entry after the list loads. */
  @Input() selectedID: number = null;

  /** Passes through to the PrimeNG dropdown and action buttons. */
  @Input() disabled: boolean = false;

  /** Prevents selection change without hiding the dropdown. */
  @Input() readonly: boolean = false;

  /** Toggles label visibility. */
  @Input() showLabel: boolean = true;

  /** Toggles the pencil/edit action button. */
  @Input() showEditButton: boolean = true;

  /** Toggles the plus/add action button. */
  @Input() showAddButton: boolean = true;

  /** Toggles the search action button. */
  @Input() showSearchButton: boolean = true;

  /**
   * Opt-in (2026-09-21): shows a small badge next to `labelKey` when the selected
   * company's discount tier is `key_partner`/`key_account` -- the two tiers whose
   * document pricing actually differs from the standard/base discount every other
   * customer gets (see `docs/PROMPT_customer-level-discount-auto-set.md`'s "Concept
   * map"). `standard`/`new`/`edge`/no tier all price at the same base terms, so no
   * badge shows for them -- a badge on every selection would just be noise. Default
   * `false`: this same component is reused for the issuing/vendor/print-report/etc.
   * selectors elsewhere in the app, where a "discount tier" concept doesn't apply at
   * all. Enable it only at call sites that bind `DOCCLIENTSID` (the buyer whose tier
   * actually drives pricing).
   */
  @Input() showTierBadge: boolean = false;

  /** The selected company's discount tier, fetched only when `showTierBadge` is set. */
  customerTier: AgentCustomerTier | null = null;

  /** `true` when a company is currently selected. */
  get isThereSelection(): boolean { return this.selectedID != null; }

  /** Short badge text for `customerTier` -- `null` (no badge) for every tier except
   *  the two that actually get elevated pricing. */
  get tierBadgeText(): string | null {
    switch (this.customerTier) {
      case 'key_partner': return 'Ключовий партнер';
      case 'key_account': return 'Ключовий клієнт';
      default: return null;
    }
  }

  /** Explains the badge on hover -- same wording as `edit-agent-customer-tier-tab`'s
   *  own tier-option labels, so the two screens describe the same fact consistently. */
  get tierBadgeTooltip(): string {
    switch (this.customerTier) {
      case 'key_partner': return 'Ключовий партнер (key_partner) — максимальна знижка. Ціни в документах враховують цей рівень.';
      case 'key_account': return 'Ключовий клієнт (key_account) — знижка за домовленістю. Ціни в документах враховують цей рівень.';
      default: return '';
    }
  }

  /** Emits the selected company's `CLIID` on change or clear. */
  @Output() public selectedCompanyID: EventEmitter<number> = new EventEmitter();

  /** Emits the main-company `CLIID` when the selected company has `CASTCATID > 0`. */
  @Output() public selectedMainCompanyID: EventEmitter<number> = new EventEmitter();

  constructor(
    private cliApi: ClientApiService,
    private router: Router,
    private location: Location
  ) { }

  /**
   * Checks `_clientMC.CASTCATID`: if `> 0`, pushes the company's `CLIID` into
   * `clientId$` to trigger the `getMainIdByClientId` pipeline and emit
   * `selectedMainCompanyID`.
   */
  private _selectCompany(): void {
    if (this._clientMC?.CASTCATID > 0) {
      this.clientId$.next(this._clientMC.CLIID);
    }
    this._refreshTierBadge();
  }

  /**
   * Fetches `customerTier` for the currently selected company (2026-09-21), only when
   * `showTierBadge` is set -- every other caller of this shared component never issues
   * this request at all. Resolves through `ClientApiService.getDiscountTier()`, the
   * same Main-Company-resolving route `DocItemPricingService.priceRow()` and
   * `edit-document-content`'s own tier lookup already use, so the badge always agrees
   * with what pricing actually applies. A lookup failure or no selection both clear the
   * badge rather than showing a stale one.
   */
  private _refreshTierBadge(): void {
    if (!this.showTierBadge || !this._clientMC?.CLIID) {
      this.customerTier = null;
      return;
    }

    this.cliApi.getDiscountTier(this._clientMC.CLIID).pipe(
      catchError(() => of({ tier: null as AgentCustomerTier | null })),
      takeUntil(this.destroy$)
    ).subscribe(result => { this.customerTier = result?.tier ?? null; });
  }

  /**
   * Fetches the full or parent-filtered company list, resolves `_clientMC` from
   * `selectedID` after load, then calls `_selectCompany()`. Cancels any prior
   * in-flight request before issuing a new one. Resets `_loading` on success or error.
   */
  private _reloadComponentData(): void {
    this._dataLoaded = true;
    this._reloadCancel$.next();
    this._loading = true;
    (this.parentCompany
      ? this.cliApi.getCliChildList(this.parentCompany)
      : this.cliApi.getCliList()
    ).pipe(
      map(data => {
        this._clientslistMC = data;
        this.totalRecordsMC = data.length;
        this._loading = false;
        this._clientMC = (this.selectedID && this._clientslistMC)
          ? this._clientslistMC.find(x => x.CLIID === this.selectedID) ?? null
          : null;
        this._selectCompany();
      }),
      takeUntil(this._reloadCancel$),
      takeUntil(this.destroy$)
    ).subscribe({
      error: err => {
        console.error('*** CompanyselectorComponent _reloadComponentData error:', err);
        this._loading = false;
      }
    });
  }

  /**
   * Reacts to `@Input()` changes: `parentCompany` triggers a full list reload;
   * `selectedID` re-syncs `_clientMC` from the loaded list without a server round-trip.
   */
  ngOnChanges(changes: SimpleChanges): void {
    for (const propName of Object.keys(changes)) {
      const change = changes[propName];

      switch (propName) {
        case 'parentCompany':
          this._reloadComponentData();
          break;

        case 'selectedID':
          if (!this.selectedID) { break; }
          if (this._clientslistMC) {
            this._clientMC = this._clientslistMC.find(x => x.CLIID === this.selectedID) ?? null;
            this._selectCompany();
          } else if (!this._loading) {
            // The list has never loaded successfully for this instance -- e.g. the
            // initial `getCliList()`/`getCliChildList()` request errored out (silently,
            // per `_reloadComponentData()`'s own catchError) -- so there is nothing to
            // resolve `selectedID` against and no future load queued to retry it later.
            // Without this, a selection made after that point (e.g. a manager picking a
            // buyer through the "Поиск контрагента" search widget) sets `selectedID`
            // correctly but the dropdown is stranded showing the placeholder forever,
            // even though the caller's own model (`newDocument.DOCCLIENTSID`) is
            // correct. Retrying here is safe even while a load IS already in flight
            // ({@link _loading} guards that case) -- `_reloadComponentData()`'s own
            // callback always resolves `_clientMC` from the current `selectedID`, so
            // that in-flight request self-heals once it lands regardless.
            this._reloadComponentData();
          }
          break;
      }
    }
  }

  /**
   * Completes `destroy$` to cancel all active subscriptions (list load and
   * `clientId$` pipeline).
   */
  ngOnDestroy(): void {
    console.log('*** CompanyselectorComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets up the debounced `clientId$` → `getMainIdByClientId` → `selectedMainCompanyID`
   * pipeline with a per-inner-request `catchError` guard, then triggers the initial
   * company list load if `ngOnChanges` hasn't already (see `_dataLoaded`'s comment --
   * this covers the caller that never binds `[parentCompany]` at all).
   */
  ngOnInit(): void {
    environment.production || console.log('*** CompanyselectorComponent ngOnInit (parentCompany): ' + this.parentCompany);
    environment.production || console.log('*** CompanyselectorComponent ngOnInit (selectedID): ' + this.selectedID);

    if (!this._dataLoaded) {
      this._reloadComponentData();
    }

    this.clientId$.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      switchMap(id =>
        this.cliApi.getMainIdByClientId(id).pipe(
          map(data => {
            environment.production || console.log('*** CompanyselectorComponent _mainIdQuery: ' + JSON.stringify(data));
            if (data && data[0]) {
              this.selectedMainCompanyID.emit(data[0].CLIID);
            }
          }),
          catchError(err => {
            console.error('*** CompanyselectorComponent _mainIdQuery error:', err);
            return EMPTY;
          })
        )
      ),
      takeUntil(this.destroy$)
    ).subscribe();
  }

  /**
   * Updates `selectedID`, emits via `selectedCompanyID`, then calls `_selectCompany()`
   * so `selectedMainCompanyID` fires when the picked company has `CASTCATID > 0`.
   * Emits `null` when the model is cleared.
   */
  onChangeMainCompany(_e: unknown): void {
    environment.production || console.log('*** CompanyselectorComponent onChangeMainCompany: ' + JSON.stringify(this._clientMC));
    if (this._clientMC) {
      this.selectedID = this._clientMC.CLIID;
      this.selectedCompanyID.emit(this.selectedID);
      this._selectCompany();
    } else {
      this.selectedID = null;
      this.selectedCompanyID.emit(null);
    }
  }

  /**
   * Nullifies `selectedID` and `_clientMC`, then emits `null` via `selectedCompanyID`.
   */
  onClearMainCompany(_e: unknown): void {
    console.log('*** CompanyselectorComponent onClearMainCompany: ' + JSON.stringify(this._clientMC));
    this.selectedID = null;
    this._clientMC = null;
    this.customerTier = null;
    this.selectedCompanyID.emit(null);
  }

  /** Shows the split-button menu with two options: manual create or paste-to-create.
   *  Main click navigates to manual add-client form. */
  onAddButton(_e: unknown): void {
    console.log('*** CompanyselectorComponent onAddButton');
    this.router.navigate(['/edit-client', 0]);
  }

  /** Opens the paste-to-create dialog in 'new-company' mode. */
  onAddPasteToCreate(): void {
    console.log('*** CompanyselectorComponent onAddPasteToCreate');
    if (this.pasteToCreateDialog) {
      this.pasteToCreateDialog.open();
    }
  }

  /** Called when AgentPasteToCreateComponent emits a successful create result. */
  onPasteToCreateCompleted(result: AgentQuickCreateResult): void {
    console.log('*** CompanyselectorComponent onPasteToCreateCompleted:', result);
    if (result.cliId !== null) {
      // Pre-select the newly created company in the selector
      this.selectedID = result.cliId;
      this.selectedCompanyID.emit(result.cliId);
    }
  }

  /** Opens the edit-client form for the currently selected company in a new browser
   *  tab, so the selector's own form stays open behind it (2026-09-14, owner: same-tab
   *  navigation away from an in-progress form was uncomfortable — same fix as
   *  `DocumentselectorComponent.onDocumentOpenButton`). */
  onEditButton(_e: unknown): void {
    console.log('*** CompanyselectorComponent onEditButton (selectedID): ' + this.selectedID);
    // `Router.serializeUrl()` only turns a UrlTree into a path string -- it
    // knows nothing about the app's LocationStrategy, so it never adds the
    // `#` this app's HashLocationStrategy (`useHash: true`, app-routing.module.ts)
    // requires. `Location.prepareExternalUrl()` is the strategy-aware step
    // that does -- without it `window.open()` opens a plain path Apache has
    // no route for (404), confirmed live 2026-09-15 (`/edit-client/:id`).
    const path = this.router.serializeUrl(this.router.createUrlTree(['/edit-client', this.selectedID]));
    window.open(this.location.prepareExternalUrl(path), '_blank');
  }

  /** Opens the advanced-search dialog. */
  onSearchButton(_e: unknown): void {
    console.log('*** CompanyselectorComponent onSearchButton');
    this.searchDisplay = true;
  }

  /**
   * Handles a company picked from the advanced-search dialog: syncs `_clientMC` from
   * the loaded list before calling `_selectCompany()`, then emits `selectedCompanyID`.
   */
  onSearchClientId(e: { CLIID: number }): void {
    console.log('*** CompanyselectorComponent onSearchClientId: ' + JSON.stringify(e));
    if (e?.CLIID != null) {
      this.selectedID = e.CLIID;
      this._clientMC = this._clientslistMC?.find(x => x.CLIID === e.CLIID) ?? null;
      this._selectCompany();
      this.selectedCompanyID.emit(this.selectedID);
    }
  }
}
